import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { UserService } from './user.service';
import jwt_decode from 'jwt-decode';

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private userService: UserService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const userToken = this.userService.getUser().token;

    if (userToken) {
      // Check if token is expired (proactive refresh)
      if (this.isTokenExpired(userToken)) {
        console.log('Token is expired or about to expire, refreshing proactively...');
        
        // Refresh token before making the request
        return from(this.userService.refreshToken()).pipe(
          switchMap((newToken: string) => {
            const modifiedReq = req.clone({
              headers: req.headers.set('Authorization', `Bearer ${newToken}`),
            });
            return next.handle(modifiedReq).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                  return this.handle401Error(modifiedReq, next);
                }
                return throwError(error);
              })
            );
          }),
          catchError((error) => {
            console.error('Proactive token refresh failed:', error);
            return throwError(error);
          })
        );
      }

      // Token is still valid, attach it to the request
      const modifiedReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${userToken}`),
      });
      return next.handle(modifiedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return this.handle401Error(modifiedReq, next);
          }
          return throwError(error);
        })
      );
    }
    return next.handle(req);
  }

  /**
   * Check if the JWT token is expired or about to expire
   * Uses a 60-second buffer to refresh slightly before actual expiration
   * @param token The JWT token to check
   * @returns true if token is expired or will expire within 60 seconds
   */
  private isTokenExpired(token: string): boolean {
    try {
      const decoded: any = jwt_decode(token);
      
      if (!decoded.exp) {
        console.warn('Token does not have expiration time, treating as expired');
        return true;
      }

      // exp is in seconds, convert to milliseconds
      const expirationTime = decoded.exp * 1000;
      const now = Date.now();
      
      // Buffer time: 60 seconds (refresh if less than 60 seconds remaining)
      const bufferTime = 60 * 1000;
      const isExpired = (expirationTime - now) < bufferTime;
      
      if (isExpired) {
        console.log(`Token expiration check: expired=${(expirationTime - now) < 0}, 
          expiresIn=${Math.round((expirationTime - now) / 1000)}s`);
      }
      
      return isExpired;
    } catch (error) {
      console.error('Error decoding token for expiration check:', error);
      // If we can't decode the token, treat it as expired (safer approach)
      return true;
    }
  }

  /**
   * Handle 401 Unauthorized errors by refreshing the token and retrying
   * @param request The original HTTP request
   * @param next The HTTP handler
   * @returns Observable with the retried request or error
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // Convert the Promise to an Observable using from()
      return from(this.userService.refreshToken()).pipe(
        switchMap((newToken: string) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);

          // Retry the original request with the new token
          const clonedRequest = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${newToken}`),
          });

          return next.handle(clonedRequest);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          // Logout is handled in UserService.refreshToken()
          return throwError(error);
        })
      );
    } else {
      // Wait for the token refresh to complete, then retry
      return this.refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((newToken: string) => {
          const clonedRequest = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${newToken}`),
          });

          return next.handle(clonedRequest);
        }),
        catchError((error) => {
          // If token is still invalid, let the error propagate
          return throwError(error);
        })
      );
    }
  }
}
